<template>
  <div class="god_page_container">
    <h2>Usuarios</h2>
    <div id="selector_container">

      <input
        type="text"
        class="input_200"
        v-model="user_email"
        placeholder="Email del usuario"
      />

      <input
        type="text"
        class="input_200"
        v-model="user_name"
        placeholder="Nombre del usuario"
      />

      <button @click="getUsers()" class="input_200 main_button">Buscar</button>

      <button @click="addUser()" class="input_200 main_button">
        Añadir usuario
        <img class="action_icon_white" src="@/assets/images/ic_add.png" />
      </button>

    </div>
    <div class="table-container" >
      <table>
        <thead>
          <tr>
              <th class="small">ID</th>
              <th class="big">Email</th>
              <th class="big">Nombre</th>
              <th class="small">Tipo</th>
              <th class="small">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="(item, index) in users"
            :key="item.ID"
            :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
          >
            <td>{{ item.ID }}</td>
            <td>{{ item.user_email }}</td>
            <td>{{ item.name }}</td>
            <td>{{ traducirTipo(item.user_type, item.activo) }}</td>
            <td>
              <img class="action_icon" src="@/assets/images/ic_reservation_edit.png" @click="onEditClicked(item)"/>
              <img class="action_icon" src="@/assets/images/ic_action_reservations.png" @click="onReservationsClicked(item)"/>
              <img class="action_icon" src="@/assets/images/ic_action_wallet.png" @click="onWalletClicked(item)"/>
              <img class="action_icon" src="@/assets/images/ic_action_delete.png" @click="onHideClicked(item)"/>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="users.length === 0" style="display: flex; justify-content: center; margin-top: 1rem;">
      <label>No hay ningún usuario con ese email</label>
    </div>

    <div style="display: flex; justify-content: center; margin-top: 1rem;">
      <button @click="exportUsersXLS()" class="main_button">Exportar todos los usuarios</button>
    </div>

    <ErrorPopup :errorMessage="error" />
    <TwoButtonPopup
      :title="twoButtonTitle"
      :message="twoButtonMessage"
      :acceptButton="twoButtonAcceptButton"
      @acceptCallback="() => twoButtonConfirmed()"
      :cancelButton="twoButtonCancelButton"
      @cancelCallback="() => twoButtonCancelled()"
      />

  </div>

</template>

<script setup>

  import { ref, nextTick, watch } from 'vue'
  import { useLoadingStore } from "@/stores/loadingStore";
  import { useRouter, useRoute } from 'vue-router';
  import { refreshToken } from "@/services/authService";
  import ErrorPopup from "@/components/common/ErrorPopup.vue";
  import TwoButtonPopup from "@/components/common/TwoButtonPopup.vue";
  import * as XLSX from "xlsx";
  import { saveAs } from "file-saver";

  const error = ref(null);
  const users = ref([]);
  const loadingStore = useLoadingStore();
  const router = useRouter();
  const route = useRoute();
  const user_email = ref(route.query.user_email || '');
  const user_name = ref(route.query.user_name || '');

  watch([user_email, user_name], () => {
    router.replace({
      query: {
        ...route.query,
        user_email: user_email.value,
        user_name: user_name.value
      }
    });
  });

  const twoButtonTitle = ref("");
  const twoButtonMessage = ref("");
  const twoButtonAcceptButton = ref("");
  const twoButtonCancelButton = ref("");
  let twoButtonConfirmed = null;
  let twoButtonCancelled = null;

  const getUsers = async () => {
    if (user_email.value === '' && user_name.value === '') {
      showError("Por favor, indica un email o un nombre para buscar.");
      return;
    }
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response = await getUsersInAPI();

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await getUsersInAPI();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        showError("Se ha producido un error buscando usuarios.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
        users.value = [];
      } else {
        error.value = null;
        users.value = data.users;
      }
    } catch (exception) {
      error.value = exception.message;
    } finally {
      loadingStore.setLoading(false);
    }
  };

  async function getUsersInAPI() {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/users/get?user_id="+localStorage.getItem('user_id')+"&user_email="+user_email.value+"&user_name="+user_name.value, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

  const showError = async (message) => {
    error.value = null;
    await nextTick();
    error.value = message;
  };

  const onEditClicked = (item) => {
    router.push({ name: 'UserDetails', params: { userid: item.ID } });
  };

  const onReservationsClicked = (item) => {
    router.push({ name: 'Reservations', query: { user_email: item.user_email } });
  };

  const onWalletClicked = (item) => {
    router.push({ name: 'Wallet', query: { user_email: item.user_email } });
  };

  const onHideClicked = async (item) => {
    twoButtonTitle.value = item.activo ? "Desactivar usuario" : "Activar usuario";
    twoButtonMessage.value = item.activo ? "¿Estás seguro de que deseas desactivar este usuario?" : "¿Estás seguro de que deseas activar de nuevo al usuario?";
    twoButtonAcceptButton.value = "Aceptar";
    twoButtonCancelButton.value = "Cancelar";
    twoButtonConfirmed = () => {
      twoButtonTitle.value = "";
      twoButtonMessage.value = "";
      twoButtonAcceptButton.value = "";
      twoButtonCancelButton.value = "";
      changeUserVisibilityConfirmed(item);
    };
    twoButtonCancelled = () => {
      twoButtonTitle.value = "";
      twoButtonMessage.value = "";
      twoButtonAcceptButton.value = "";
      twoButtonCancelButton.value = "";
    }
  }

  const changeUserVisibilityConfirmed = async (item) => {
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response = await changeUserVisibilityInAPI(item);

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await changeUserVisibilityInAPI(item);
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        loadingStore.setLoading(false);
        showError("Se ha producido un error cambiando el estado del usuario.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
      } else {
        error.value = null;
      }
      getUsers();
    } catch (exception) {
      error.value = exception.message;
    }
  };

  const changeUserVisibilityInAPI = async (item) => {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/users/edit_visibility", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
        body: JSON.stringify({
          "user_id": localStorage.getItem('user_id'),
          "user_to_edit": item.ID,
          "visibility": item.activo ? false : true
        }),
      });
  };

  function traducirTipo(userType, active) {
    if (userType === 'empresa') {
      if (active) {
        return 'Empresa';
      } else {
        return '🚫 Empresa 🚫';
      }
    } else {
      if (active) {
        return 'Usuario';
      } else {
        return '🚫 Usuario 🚫';
      }
    }
  }

  async function addUser() {
    router.push({ name: 'NewUser' });
  }

  const exportUsersXLS = async () => {
    loadingStore.setLoading(true);
    try {
      let response = await getAllUsersInAPI();

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await getAllUsersInAPI();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        showError("Ha ocurrido un error generando el Excel con los usuarios.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        showError("Ha ocurrido un error generando el Excel con los usuarios.");
      } else {
        exportXLS(data.users);
      }
    } catch (exception) {
        showError("Ha ocurrido un error generando el Excel con los usuarios.");
    } finally {
      loadingStore.setLoading(false);
    }
  }

  const exportXLS = (users) => {
    const flatUsersData = [];
    const flatCompaniesData = [];

    users.filter(user => user.user_type === 'usuario').forEach(user => {
      let entry = {
        "Email": user.user_email,
        "Nombre": user.name,
        "Activo": user.activo ? "SI" : "NO",
        "Ciudad": user.city
      };

      flatUsersData.push(entry);
    });

    users.filter(user => user.user_type === 'empresa').forEach(user => {
      let entry = {
        "Email": user.user_email,
        "Nombre": user.name,
        "Activo": user.activo ? "SI" : "NO",
        "Nombre facturacion": user.invoice_name,
        "Dirección facturacion": user.invoice_address,
        "Ciudad facturacion": user.invoice_city,
        "CIF": user.invoice_cif,
      };

      flatCompaniesData.push(entry);
    });

      // Crear la hoja de Excel
    const worksheetUsers = XLSX.utils.json_to_sheet(flatUsersData);
    const worksheetCompanies = XLSX.utils.json_to_sheet(flatCompaniesData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheetUsers, "Usuarios");
    XLSX.utils.book_append_sheet(workbook, worksheetCompanies, "Empresas");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(data, "users_god.xlsx");
  }

  async function getAllUsersInAPI() {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/users/get_to_export?user_id="+localStorage.getItem('user_id'), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

</script>

<style scoped>

#selector_container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding-top: 1rem;
  justify-content: space-between;
}

.input_200 {
  box-sizing: border-box;
  min-width: 200px;
  flex: 1 1 200px;
  padding: 8px;
  width: 100%;
  line-height: 1.2;
  height: 50px;
  max-height: 50px;
}

.table-container {
  overflow-x: auto;
  width: 100%;
}

table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 30px;
  min-width: 800px;
}

th {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
  background-color: var(--gray);
  color: white;
  border: 0px;
}

tfoot {
  background-color: var(--gray);
  color: white;
  border: 0px;
}
.big {
  width: 20%;
}

.small {
  width: 10%;
}

tr{
  cursor: pointer;
}

td {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
}

.bg-gray-100 {
  background-color: var(--almost-white);
}

.bg-white {
  background-color: white;
}

.action_icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin: 0px 5px;
}

.action_icon_white {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-left: 10px;
  filter: brightness(0) invert(1);
}

@media (max-width: 768px) {
  #selector_container > * {
    flex: none !important;
  }

  .input_200 {
    width: 100%;
  }

  table {
    width: 100%;
    min-width: 0px;
  }

}

</style>
